import React, { useState } from 'react';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';

import _ from 'lodash';
import { Col } from 'react-bootstrap';

import { QuantityGetDto, StockTransactionStatus } from 'svila-common';

import DragableInventory from './DragableInventory';
import StatusModal from './StatusModal';
import ColumnOverlay from './ColumnOverlay';

interface Types {
  columnName: string;
  distinctBatches: QuantityGetDto[];
  setDistinctBatches: (items: QuantityGetDto[]) => void;
  rowId: string;
  title: string;
  color?: string;
  palletsPerStatus: any;
  handleClick?: any;
  isChecked?: any;
}

export interface PopupPacket {
  item: QuantityGetDto;
  onYes: (originalItem: QuantityGetDto, oldItem: QuantityGetDto, newItem?: QuantityGetDto) => void;
  column: StockTransactionStatus;
  onNo: (visible: PopupPacket | null) => void;
}

export default function DropableColumn({
  columnName,
  distinctBatches,
  setDistinctBatches,
  rowId,
  title,
  color,
  palletsPerStatus,
  handleClick,
  isChecked,
  getProducts,
}: Types): JSX.Element {
  const [itemInPopup, setItemInPop] = useState<PopupPacket | null>(null);
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: rowId,
    drop: (item) => {
      if (columnName != item.status) {
        showPopup(item);
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
    hover: (item, monitor) => {
      !!monitor.isOver;
      !!monitor.canDrop;
    },
  });

  const addElementToColumn = (
    originalItem: QuantityGetDto,
    oldItem: QuantityGetDto,
    newItem?: QuantityGetDto,
  ): void => {
    const cloned = _.cloneDeep(distinctBatches);

    const filtered = cloned.filter((el) => {
      if (el.tempId !== originalItem.tempId) {
        return true;
      }

      return false;
    });

    if (
      (oldItem.fullDuUnits ?? 0) !== 0 ||
      (oldItem.singleDpUnits ?? 0) !== 0 ||
      (oldItem.singleCuUnits ?? 0) !== 0
    ) {
      filtered.push(oldItem);
    }

    if (
      newItem &&
      ((newItem.fullDuUnits ?? 0) !== 0 ||
        (newItem.singleDpUnits ?? 0) !== 0 ||
        (newItem.singleCuUnits ?? 0) !== 0)
    ) {
      filtered.push(newItem);
    }

    setItemInPop(null);
    setDistinctBatches(filtered);
    getProducts();
  };

  const showPopup = (orI: QuantityGetDto): void => {
    const packet = {
      item: orI,
      column: columnName,
      onYes: addElementToColumn,
      onNo: setItemInPop,
    };
    setItemInPop(packet);
  };

  const returnProductForColumn = (): JSX.Element[] => {
    const filtered = distinctBatches.filter((distinctBatch) => distinctBatch.status === columnName);

    const jsxs = filtered.map((item) => {
      return (
        <DragableInventory
          isChecked={isChecked.includes(item.tempId)}
          handleClick={handleClick}
          key={item.tempId}
          item={item}
          rowId={rowId}
          title={title}
        />
      );
    });

    return jsxs;
  };

  return (
    <Column ref={drop}>
      {title && <TitleColumn color={color}>{title}</TitleColumn>}
      {returnProductForColumn()}
      {itemInPopup && <StatusModal initialData={itemInPopup} />}
      {isOver && !canDrop && <ColumnOverlay background={'#D81F21'} />}
      {isOver && canDrop && <ColumnOverlay background={'#fff'} borderColor={color} />}
      {!isOver && canDrop && <ColumnOverlay background={'#F4F4F4'} />}
    </Column>
  );
}

const TitleColumn = styled.h1`
  position: relative;
  color: #343434;
  font-size: 15px;
  line-height: 16px;
  font-weight: 700;
  text-align: center;
  z-index: 2;
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 5px;
    left: 5px;
    right: 5px;
    height: 9px;
    background-color: ${(props) => {
    return props.color;
  }};
    border-radius: 10px;
  }
`;

const Column = styled(Col)`
  width: 100%;
  border: 1px solid #f4f4f4;
  background: #f4f4f4;
  border-radius: 20px;
  margin: 0 5px;
  padding: 5px;
`;
